import {
  formatCurrencyBRL,
  parseCurrencyInput,
} from '@/features/quotes/utils/group-life/format';

/**
 * Plano na visão do front — só o que o cálculo do capital precisa.
 * Compatível por estrutura com `GroupLifeFrontPlan` e com o `plan` do contexto
 * de cotação.
 */
export type CapitalPlanContext = {
  modality?: string;
  uniformCapital?: string;
  numberOfSalaries?: string;
  minimumIndividualCapital?: string;
  maximumIndividualCapital?: string;
};

/**
 * Sobe ao mínimo ou desce ao máximo individual do plano. Sem esses campos,
 * o valor fica como está — R$ 500.000 não é teto de exibição.
 */
export function applyIndividualCapitalBounds(
  amount: number,
  plan?: CapitalPlanContext
): number {
  const minimo = parseCurrencyInput(plan?.minimumIndividualCapital ?? '');
  const maximo = parseCurrencyInput(plan?.maximumIndividualCapital ?? '');
  let capital = amount;
  if (minimo != null && minimo > 0 && capital < minimo) capital = minimo;
  if (maximo != null && maximo > 0 && capital > maximo) capital = maximo;
  return capital;
}

/** Aplica o piso e o teto do plano a um capital já resolvido, em BRL. */
export function boundDisplayedCapital(
  capital: string | undefined,
  plan?: CapitalPlanContext
): string {
  const armazenado = capital?.trim() ?? '';
  if (plan?.modality !== 'MULTIPLE_SALARY') return armazenado;
  const amount = parseCurrencyInput(armazenado);
  if (amount == null || amount <= 0) return armazenado;
  return formatCurrencyBRL(applyIndividualCapitalBounds(amount, plan));
}

/**
 * Capital da vida na listagem.
 *
 * Em Múltiplo Salarial o valor guardado é o salário. A coluna multiplica pela
 * quantidade de salários do plano quando `multiplySalary` é verdadeiro e, em
 * seguida, respeita o capital mínimo e o máximo individual. Sem essa flag o
 * número aparece como veio.
 *
 * Uniforme sem valor na vida cai no capital uniforme do plano. O gatilho de
 * R$ 500.000 não reescreve a coluna.
 */
export function resolveMemberDisplayCapital(
  capital: string | undefined,
  plan?: CapitalPlanContext,
  options?: { multiplySalary?: boolean }
): string {
  const armazenado = capital?.trim();
  if (!armazenado) {
    if (plan?.modality === 'UNIFORM' && plan.uniformCapital?.trim()) {
      return plan.uniformCapital.trim();
    }
    return '';
  }
  if (!plan?.modality || plan.modality === 'UNIFORM') {
    return armazenado;
  }
  if (plan.modality === 'MULTIPLE_SALARY') {
    if (!options?.multiplySalary) return armazenado;
    const qtdSalarios =
      parseInt((plan.numberOfSalaries ?? '').replace(/\D/g, ''), 10) || 0;
    const salario = parseCurrencyInput(armazenado) ?? 0;
    if (qtdSalarios <= 0 || salario <= 0) return armazenado;
    const multiplied = Math.round(salario * qtdSalarios * 100) / 100;
    return formatCurrencyBRL(applyIndividualCapitalBounds(multiplied, plan));
  }

  const efetivo = parseCurrencyInput(armazenado) ?? 0;
  if (efetivo <= 0) return armazenado;

  return formatCurrencyBRL(efetivo);
}
